// Persisted settings store + the ⚙ cog panel. All persisted options live
// here under one JSON localStorage key (previously scattered across `cols`,
// `preview`, `renamePattern` keys — migrated below so existing
// users keep their values). Defaults are declared in one place so every
// caller sees the same fallback.
const STORAGE_KEY = 'ms-settings';

const DEFAULTS = {
  cols: 6,
  preview: true,
  renamePattern: 'my_gallery_###',
  displayMode: 'grid',   // 'grid' | 'rows'
  rowH: 200,             // justified-rows row height, px (clamp ~60-600)
  vidSegments: 5,        // video sampler: how many segments to hop across
  vidSegSeconds: 2,      // seconds of each segment (video-timeline seconds)
  vidRate: 2,            // playbackRate while sampling
  hoverDelay: 400,       // ms a mouse must dwell on a video cell before hover-play starts
  panelSide: 'right',    // 'left' | 'right' — which edge the side panel docks to
  panelFrac: 0.4,        // side panel width as a fraction of viewport width, clamp ~0.15-0.75
  treeShow: false,       // directory tree panel visibility
  treeFrac: 0.22,        // tree panel width as a fraction of viewport width, clamp ~0.1-0.5
  lastSubpath: [],       // segments from the remembered root to the last open subfolder
};

// Values from the scattered legacy localStorage keys, typed like DEFAULTS.
function migrateLegacy() {
  const legacy = {};
  const cols = localStorage.getItem('cols');
  if (cols !== null && cols !== '') legacy.cols = +cols;
  const preview = localStorage.getItem('preview');
  if (preview !== null) legacy.preview = preview !== '0';
  const pattern = localStorage.getItem('renamePattern');
  if (pattern !== null) legacy.renamePattern = pattern;
  return legacy;
}

let values;
try {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    values = { ...DEFAULTS, ...JSON.parse(raw) };
  } else {
    values = { ...DEFAULTS, ...migrateLegacy() };
    persist();
    for (const k of ['cols', 'preview', 'renamePattern']) localStorage.removeItem(k);
  }
} catch {
  values = { ...DEFAULTS };
}

function persist() {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(values)); } catch {}
}

const listeners = new Map();   // key -> Set<fn>

export const settings = {
  get(key) { return values[key]; },
  set(key, val) {
    if (values[key] === val) return;
    values[key] = val;
    persist();
    for (const fn of listeners.get(key) || []) fn(val);
  },
  onChange(key, fn) {
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key).add(fn);
    return () => listeners.get(key)?.delete(fn);
  },
};

/* ---------- ⚙ cog panel ----------
 * A small anchored popover (not a <dialog>: same outside-click-to-close
 * semantics as the #otherWarn popup, and it must not trap focus). Callers
 * use `addSection(title)` to append their own labeled section of rows. */
export function initSettingsPanel() {
  const btn = document.getElementById('settingsBtn');
  const panel = document.getElementById('settingsPanel');
  const body = document.getElementById('settingsBody');

  // Anchored in JS, not CSS: the panel isn't a child of the button (a <button>
  // can't contain the panel's own buttons) and the toolbar can wrap to >1 row.
  function position() {
    const r = btn.getBoundingClientRect();
    panel.style.top = r.bottom + 6 + 'px';
    // Keep it on-screen on narrow viewports even though it's right-anchored.
    panel.style.right = Math.max(8, innerWidth - r.right) + 'px';
  }

  function open() { position(); panel.hidden = false; btn.classList.add('active'); }
  function close() { panel.hidden = true; btn.classList.remove('active'); }

  // Listeners are attached once and gated on panel.hidden (same style as the
  // document-level #otherWarn closer in app.js) — no add/remove churn per open.
  // The button's stopPropagation keeps its own opening click from reaching the
  // outside-click closer below.
  btn.addEventListener('click', (e) => { e.stopPropagation(); panel.hidden ? open() : close(); });
  document.getElementById('settingsClose').addEventListener('click', close);
  addEventListener('click', (e) => { if (!panel.hidden && !panel.contains(e.target)) close(); });
  addEventListener('resize', () => { if (!panel.hidden) position(); });
  // Capture phase so Escape closes the panel instead of reaching the app-level
  // key handler (which would clear the preview panel).
  addEventListener('keydown', (e) => {
    if (!panel.hidden && e.key === 'Escape') { e.stopPropagation(); close(); }
  }, true);

  function addSection(title) {
    const section = document.createElement('div');
    section.className = 'set-section';
    const h = document.createElement('h3');
    h.textContent = title;
    const rows = document.createElement('div');
    rows.className = 'set-rows';
    section.append(h, rows);
    body.appendChild(section);
    return rows;
  }

  function addNumber(rows, { label: labelText, key, min, max, step, suffix = '' }) {
    const row = document.createElement('div');
    row.className = 'set-row';
    const label = document.createElement('label');
    label.textContent = labelText;
    const id = 'set-' + key;
    label.htmlFor = id;
    const input = document.createElement('input');
    input.type = 'number'; input.id = id;
    input.min = min; input.max = max; input.step = step;
    input.value = settings.get(key);
    input.addEventListener('change', () => {
      const v = Math.min(max, Math.max(min, +input.value || min));
      input.value = v;
      settings.set(key, v);
    });
    row.append(label, input);
    if (suffix) { const s = document.createElement('b'); s.className = 'set-val'; s.textContent = suffix; row.appendChild(s); }
    rows.appendChild(row);
    return row;
  }

  function addSelect(rows, { label: labelText, key, options }) {
    const row = document.createElement('div');
    row.className = 'set-row';
    const label = document.createElement('label');
    label.textContent = labelText;
    const sel = document.createElement('select');
    sel.innerHTML = options.map(([v, text]) => `<option value="${v}">${text}</option>`).join('');
    sel.value = settings.get(key);
    sel.addEventListener('change', () => settings.set(key, sel.value));
    row.append(label, sel);
    rows.appendChild(row);
    return row;
  }

  function addRange(rows, { label: labelText, key, min, max, step, id = 'set-' + key, format = v => v, onInput }) {
    const row = document.createElement('div');
    row.className = 'set-row';
    const label = document.createElement('label');
    label.textContent = labelText;
    label.htmlFor = id;
    const range = document.createElement('input');
    range.type = 'range'; range.id = id;
    range.min = min; range.max = max; range.step = step;
    range.value = settings.get(key);
    const out = document.createElement('b');
    out.className = 'set-val';
    out.textContent = format(settings.get(key));
    range.addEventListener('input', () => {
      const v = +range.value;
      out.textContent = format(v);
      if (onInput) onInput(v);
    });
    range.addEventListener('change', () => settings.set(key, +range.value));
    row.append(label, range, out);
    rows.appendChild(row);
    return row;
  }

  return { addSection, addNumber, addSelect, addRange, close };
}
